import { Inbox as InboxIcon } from "lucide-react";
import { usePathname, useSearchParams } from "next/navigation";
import { InboxItem } from "./InboxItem";
import {
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuSkeleton,
} from "@/components/shadcn/sidebar";
import { useState, useEffect, useMemo, useRef } from "react";
import InboxDeleteDialog from "./InboxDeleteDialog";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import type { Modifier } from "@dnd-kit/core";
import { useInboxes } from "./useInboxes";

const restrictToVerticalAxisOnly: Modifier = ({ transform }) => {
  return {
    ...transform,
    x: 0,
  };
};

const EMPTY_ARRAY: any[] = [];

export const InboxList = () => {
  const { inboxes, updateOrder, inboxOrder, isLoading } = useInboxes();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isDragging, setIsDragging] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedInboxId, setSelectedInboxId] = useState<string | null>(null);

  const sortedInboxes = useMemo(() => {
    if (!inboxes.length) return EMPTY_ARRAY;

    const sorted = [...inboxes].sort((a, b) => {
      const orderA = inboxOrder[a.id] ?? 999;
      const orderB = inboxOrder[b.id] ?? 999;
      if (orderA !== orderB) return orderA - orderB;
      return (
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );
    });

    return sorted;
  }, [inboxes, inboxOrder]);

  const [localInboxes, setLocalInboxes] = useState(sortedInboxes);

  useEffect(() => {
    if (!isDragging) {
      // Only update if the content is actually different to avoid infinite loops
      const isDifferent =
        JSON.stringify(localInboxes) !== JSON.stringify(sortedInboxes);

      if (isDifferent) {
        setLocalInboxes(sortedInboxes);
      }
    }
  }, [sortedInboxes, isDragging, localInboxes]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const displayInboxes = localInboxes;

  const handleDragStart = (event: any) => {
    setIsDragging(true);
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (active?.id !== over?.id) {
      const oldIndex = displayInboxes.findIndex(
        (item) => item.id === active?.id
      );
      const newIndex = displayInboxes.findIndex((item) => item.id === over?.id);

      const reorderedInboxes = arrayMove(displayInboxes, oldIndex, newIndex);
      const newOrder = reorderedInboxes.map((inbox) => inbox.id);

      setLocalInboxes(reorderedInboxes);
      updateOrder(newOrder);
    }

    setIsDragging(false);
  };

  if (isLoading) {
    return (
      <SidebarMenu className="list-none gap-0.5 p-0 m-0">
        <SidebarMenuItem className="list-none">
          <SidebarMenuSkeleton showIcon />
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  if (displayInboxes.length === 0) {
    return (
      <SidebarMenu className="list-none gap-0.5 p-0 m-0">
        {/* Laid out like a sidebar row, so the text lines up with the
            entries below. */}
        <div className="flex items-center gap-2 p-2 text-sm text-sidebar-foreground/50">
          <InboxIcon className="size-4 shrink-0" strokeWidth={1.25} />
          <span className="pl-1">No inboxes yet</span>
        </div>
      </SidebarMenu>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxisOnly, restrictToParentElement]}
    >
      <SortableContext
        items={displayInboxes.map((inbox) => inbox.id)}
        strategy={verticalListSortingStrategy}
      >
        <SidebarMenu className="list-none gap-0.5 p-0 m-0">
          {displayInboxes.map((inbox, index) => (
            <InboxItem
              key={inbox.id}
              inbox={inbox}
              isActive={
                (pathname?.startsWith(`/inbox/${inbox.id}`) ?? false) ||
                ((pathname?.startsWith("/cases/") ?? false) &&
                  searchParams?.get("inboxId") === inbox.id) ||
                ((pathname?.startsWith("/tasks/") ?? false) &&
                  searchParams?.get("inboxId") === inbox.id)
              }
              index={index}
              isAnyDragging={isDragging}
              badge={inbox.stats?.open_cases_count}
              onDelete={(inboxId) => {
                setSelectedInboxId(inboxId);
                setDeleteDialogOpen(true);
              }}
            />
          ))}
        </SidebarMenu>
      </SortableContext>
      {deleteDialogOpen && selectedInboxId && (
        <InboxDeleteDialog
          isOpen={deleteDialogOpen}
          onClose={() => {
            setDeleteDialogOpen(false);
            setSelectedInboxId(null);
          }}
          inbox={displayInboxes.find((i) => i.id === selectedInboxId) || null}
        />
      )}
    </DndContext>
  );
};
